






import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import { Bar } from 'vue-chartjs/legacy';
import { ChartData, ChartOptions } from 'chart.js';
import { CreateSurveyElement } from '@/models/Presentation';

@Component({
  components: { Bar }
})
export default class VerticalBarChart extends Vue {

  @Prop() surveyElement!: CreateSurveyElement;
  @Prop() values!: number[];
  @Prop() totalData!: number;

  containerSize = { width: 0, height: 0 };

  maxLines = 3;

  correct: boolean[] = [];
  key = 0;
  animationDuration = 1000;

  get labelFontSize(): number {
    // Space per answer in px
    const spacePerItem = this.containerSize.width / this.surveyElement.answers.length;

    // Longest answer length in characters per line
    const longestAnswerLength = this.surveyElement.answers.reduce((max, answer) => Math.max(max, answer.text.length), 0) / this.maxLines;

    const spacePerChar = spacePerItem / longestAnswerLength

    // Calculate the font size based on the available space and the longest answer length
    const fontSize = Math.max(Math.min(Math.floor(spacePerChar * 12/7), 16), 10); // Limit font size between 10 and 20px

    console.log(`Calculated font size: ${fontSize}px with spacePerChar: ${spacePerChar}px`);
    return fontSize
  }


  mounted() {
    this.key = 0;
    this.correct = this.surveyElement.answers.map(answer => answer.correct);

    const container = this.$refs.container as HTMLElement;
    if (!container) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        this.containerSize.width = entry.contentRect.width;
        this.containerSize.height = entry.contentRect.height;
      }
    });

    resizeObserver.observe(container);
  }

  get chartData(): ChartData {
    return {
      labels: this.surveyElement.answers.map(answer => answer.text),
      datasets: [
        {
          label: '',
          data: this.surveyElement.chartNumberType === 'PERCENT'
            ? this.values.map(value => (value / Math.max(this.totalData, 1)) * 100)
            : this.values,
          backgroundColor: this.surveyElement.answers.map(answer => answer.color),
          borderRadius: 5,
          minBarLength: 4
        }
      ]
    };
  }

  get chartOptions(): ChartOptions {
    return {
      responsive: true,
      maintainAspectRatio: false,
      animation: {
        duration: this.animationDuration
      },

      layout: {
        padding: {
          top: this.containerSize.width > 400 ? 35 : 30,
        }
      },
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          enabled: true,
          displayColors: false,
          filter: (tooltipItem) => {
            const index = tooltipItem.dataIndex;
            const text = this.surveyElement.answers[index].text;
            return !!text && text.trim().length > 0;
          },
          callbacks: {
            title: (tooltipItems) => {
              if(!tooltipItems[0]){
                return '';
              }
              const index = tooltipItems[0].dataIndex;
              const fullText = this.surveyElement.answers[index].text;

              if(fullText.length == 0) {
                return fullText;
              }

              // Wenn du automatische Zeilenumbrüche willst, splitte manuell nach Länge
              const words = fullText.split(' ');
              const lines = [];
              let line = '';
              words.forEach(word => {
                if ((line + word).length > 25) {
                  lines.push(line);
                  line = word + ' ';
                } else {
                  line += word + ' ';
                }
              });
              lines.push(line.trim());
              return lines; // Tooltip wird mehrzeilig
            },
            label: () => '', // Verstecke Wert
          },
          // Optional: Tooltip-Position direkt über Balken
          position: 'average',
          yAlign: 'center',
          padding: 10,
          titleFont: {
            size: 12,
          },
          bodyFont: {
            size: 0, // Kein Bodytext
          },
        },
        datalabels: {
          anchor: 'end',
          align: 'top',
          formatter: (value: number, context) => {
            const spacePerItem = this.containerSize.width / this.surveyElement.answers.length;

            const toFixed = spacePerItem < 50 ? 0 : 1;

            const answer = this.surveyElement.answers[context.dataIndex];
            const formattedValue = this.surveyElement.chartNumberType === 'PERCENT'
              ? `${value.toFixed(toFixed)}%`
              : value;

            return answer.correct ? `${formattedValue} ✅` : formattedValue;
          },
          color: '#000',
          clip: false,
          font: {
            size: this.labelFontSize,
          },
        }
      },
      scales: {
        x: {
          grid: { display: false, drawBorder: true, borderColor: '#000' },
          ticks: {
            display: true,
            maxRotation: 0,
            autoSkip: false,
            font: {
              size: this.labelFontSize,
            },
            color: '#000',
            callback: (value, index) => {
              return this.formatLabel(this.surveyElement.answers[index].text)
            }
          },
        },
        y: {
          suggestedMax: this.suggestedMax,
          grid: { display: false, drawBorder: false },
          ticks: { display: false },
        }
      }
    };
  }

  get suggestedMax(): number{
    return Math.max(...this.values, 1)
  }

  formatLabel(str: string): string[] {
    str = str.trim();
    const words = str.split(' ');
    let lines: string[] = [];
    let currentLine = '';

    const currentFontSize = this.labelFontSize;

    // Space per answer in px
    const spacePerItem = this.containerSize.width / this.surveyElement.answers.length;

    // Assume each character takes up 1/2 of the font size in px
    const maxCharsPerLine = Math.floor(spacePerItem / (currentFontSize / 2));

    words.forEach((word) => {
      if(word.length > maxCharsPerLine) {
        word = word.slice(0, maxCharsPerLine - 3) + '...';
      }

      const testLine = currentLine ? `${currentLine} ${word}` : word;

      if (testLine.length > maxCharsPerLine && lines.length < 3) {
        lines.push(currentLine);
        currentLine = word; // Starte eine neue Zeile mit dem aktuellen Wort
      } else {
        currentLine = testLine;
      }
    });

    lines.push(currentLine);

    if(lines.length > 3) {
      if(lines[2].length < maxCharsPerLine){
        lines[2] = lines[2] + '...';
      } else {
        lines[2] = lines[2].slice(0, lines[2].length - 2) + '...';
      }
    }

    lines.splice(3, lines.length - 3); // max 3 lines
    console.log(str, lines);
    return lines;
  }




  @Watch('surveyElement', {deep: true})
  onSurveyElementChange() {
    const newCorrects = this.surveyElement.answers.map(answer => answer.correct);
    if (this.correct.length !== newCorrects.length || this.correct.some((v, i) => v !== newCorrects[i])) {
      this.key++;
      this.correct = newCorrects;
      this.animationDuration = 0;

      this.$nextTick(() => {
        this.animationDuration = 1000;
      });
    }
  }

}
